import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowRight, Cpu, Zap } from 'lucide-react';
import { FaFacebook, FaInstagram } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import { SHOPIFY_INSTALL_URL, ARISMATE_LOGO } from './Navbar';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    product: [
      { name: 'Features', href: '/features' },
      { name: 'How It Works', href: '/how-it-works' },
      { name: 'Integrations', href: '/integrations' },
      { name: 'Custom AI', href: '/custom-ai' }
    ],
    company: [
      { name: 'Contact', href: '/contact' },
      { name: 'Install on Shopify', href: SHOPIFY_INSTALL_URL, external: true }
    ],
    legal: [
      { name: 'Privacy Policy', href: '/privacy' }
    ]
  };

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      {/* CTA Section */}
      <div className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-16">
          <motion.div 
            className="flex flex-col lg:flex-row items-center justify-between gap-6 sm:gap-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center lg:text-left">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3">
                Ready to Deploy AI?
              </h3>
              <p className="text-slate-400 text-sm sm:text-lg">
                Empower your e-commerce store with Arismate AI
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
              <a href={SHOPIFY_INSTALL_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-white font-semibold group shadow-lg shadow-teal-600/20"
                  data-testid="footer-install-btn"
                >
                  <Cpu className="w-5 h-5 mr-2" />
                  Install for Shopify
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <Link to="/contact" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="w-full sm:w-auto border-slate-700 text-slate-300 hover:text-teal-400 hover:border-teal-500 font-semibold"
                  data-testid="footer-contact-btn"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <div className="flex flex-col items-center">
                <img
                  src={ARISMATE_LOGO}
                  alt="Arismate"
                  className="brightness-0 invert"
                />
                <span className="text-[10px] font-medium text-white tracking-widest -mt-1">by arismeta</span>
              </div>
            </Link>
            <p className="text-slate-400 text-sm sm:text-base leading-relaxed mb-6">
              AI-powered sales automation for modern e-commerce stores.
            </p>
            {/* Social Links */}
            <div className="flex gap-3">
              <a href="https://www.instagram.com/arismate_/?fbclid=IwY2xjawQkeq5leHRuA2FlbQIxMQBicmlkETBQUXdEYm5CcUMydlpyUHQ1c3J0YwZhcHBfaWQBMAABHjecplWlkZv8nq5MuZW-fdg-G_PmGhFOzuK2VUF5OzzMw5hFaW3M6WznPL9G_aem_XKfjghFdIZPBrk6LnN064g#" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:text-teal-400 hover:bg-slate-700 transition-colors" data-testid="footer-instagram-link">
                <FaInstagram className="w-5 h-5" />
              </a>
              <a href="https://www.facebook.com/people/Arismate/61579516950709/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:text-teal-400 hover:bg-slate-700 transition-colors" data-testid="footer-facebook-link">
                <FaFacebook className="w-5 h-5" />
              </a>
              <a href="mailto:support@arismate.com" className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:text-teal-400 hover:bg-slate-700 transition-colors" data-testid="footer-email-link">
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="font-bold text-base sm:text-lg mb-4 sm:mb-5 text-white">Product</h4>
            <ul className="space-y-2 sm:space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-slate-400 hover:text-teal-400 transition-colors text-sm sm:text-base"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-bold text-base sm:text-lg mb-4 sm:mb-5 text-white">Company</h4>
            <ul className="space-y-2 sm:space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  {link.external ? (
                    <a 
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-400 hover:text-teal-400 transition-colors text-sm sm:text-base"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link 
                      to={link.href}
                      className="text-slate-400 hover:text-teal-400 transition-colors text-sm sm:text-base"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-bold text-base sm:text-lg mb-4 sm:mb-5 text-white">Legal</h4>
            <ul className="space-y-2 sm:space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href}
                    className="text-slate-400 hover:text-teal-400 transition-colors text-sm sm:text-base"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 sm:gap-4 text-xs sm:text-sm text-slate-400">
            <p className="text-center md:text-left">© {currentYear} Arismate by Arismeta Technology Services. All rights reserved.</p>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-teal-400" />
              <p>AI-Powered | E-commerce Optimized</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
